import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, Settings, Plus, X, Trash2, Folder } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import ContentDetailModal from '../components/modals/ContentDetailModal';
import ProgressBar from '../components/ui/ProgressBar';
import contentService from '../services/contentService';
import topicService from '../services/topicService';
import { useAuth } from '../contexts/AuthContext';
import ErrorHandler from '../utils/errorHandler';
import { useRealtimeContent } from '../contexts/RealtimeContentContext';
import { supabase } from '../lib/supabase';

// Thumbnail cache (shared across renders)
const thumbnailCache = new Map();

const CollectionsPage = () => {
  const { user } = useAuth();
  const { items: realtimeItems } = useRealtimeContent(); // Get realtime updates
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Debug component mount
  useEffect(() => {
    console.log('CollectionsPage mounted');
    console.log('User from auth:', user);
  }, []);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionContent, setCollectionContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collections, setCollections] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [deletingContent, setDeletingContent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);
  const [showDeleteCollectionModal, setShowDeleteCollectionModal] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [deletingCollection, setDeletingCollection] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [editingTitle, setEditingTitle] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  
  const [newCollection, setNewCollection] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });
  const [creating, setCreating] = useState(false);
  
  // Debug modal state
  useEffect(() => {
    if (showCreateModal) {
      console.log('Create Collection modal opened');
    }
  }, [showCreateModal]);

  // Function to refresh collection content counts (fast, without thumbnails)
  const refreshCollectionCounts = async (collectionsList) => {
    if (!collectionsList || collectionsList.length === 0) return collectionsList;
    
    try {
      console.log('🔄 Refreshing content counts for collections...', collectionsList.length, 'collections');
      const updatedCollections = await Promise.all(
        collectionsList.map(async (collection) => {
          try {
            console.log(`🔍 Counting items for collection "${collection.name}" (${collection.id})`);
            const { data: content, error: contentError } = await contentService.getContentByCollection(user.id, collection.id);
            
            if (contentError) {
              console.error(`❌ Error getting content for collection ${collection.id}:`, contentError);
              return { ...collection, content_count: 0, recent_thumbnail: null };
            }
            
            const contentCount = content?.length || 0;
            
            console.log(`📊 Collection "${collection.name}" has ${contentCount} items`);
            return { ...collection, content_count: contentCount, recent_thumbnail: null };
          } catch (error) {
            console.error(`❌ Failed to get content count for collection ${collection.id}:`, error);
            console.error('Error details:', error.message, error.stack);
            return { ...collection, content_count: 0, recent_thumbnail: null };
          }
        })
      );
      console.log('✅ Finished refreshing collection counts');
      return updatedCollections;
    } catch (error) {
      console.error('❌ Failed to refresh collection counts:', error);
      return collectionsList;
    }
  };

  // Add cached thumbnails from database (instant - single query)
  const addCachedThumbnails = async (collectionsList) => {
    if (!collectionsList || collectionsList.length === 0) return collectionsList;
    
    try {
      const collectionIds = collectionsList.map(c => c.id);
      
      // Single query to get all cached thumbnails
      const { data: thumbnailData } = await supabase
        .schema('public')
        .from('topics')
        .select('id, recent_thumbnail, thumbnail_updated_at')
        .in('id', collectionIds);
      
      // Create a map for quick lookup
      const thumbnailMap = {};
      (thumbnailData || []).forEach(t => {
        thumbnailMap[t.id] = t.recent_thumbnail;
      });
      
      // Add thumbnails to collections
      const collectionsWithThumbnails = collectionsList.map(c => ({
        ...c,
        recent_thumbnail: thumbnailMap[c.id] || null
      }));
      
      console.log(`✅ Added cached thumbnails to ${collectionsList.length} collections (instant!)`);
      return collectionsWithThumbnails;
    } catch (error) {
      console.error('Error loading cached thumbnails:', error);
      return collectionsList;
    }
  };

  // Load collections on mount
  useEffect(() => {
    const loadCollections = async () => {
      if (!user?.id) return;
      
      try {
        console.log('🔄 CollectionsPage: Loading collections for user:', user.id);
        const { data: topics } = await topicService.getUserTopics(user.id);
        console.log('📊 CollectionsPage: Loaded collections:', topics?.length || 0, 'collections');
        console.log('📋 CollectionsPage: Collections list:', topics?.map(t => ({ id: t.id, name: t.name })) || []);
        
        // Add cached thumbnails to the topics
        const topicsWithThumbnails = await addCachedThumbnails(topics || []);
        
        // Refresh content counts for all collections (fast)
        const collectionsWithCounts = await refreshCollectionCounts(topicsWithThumbnails || []);
        
        // Thumbnails are already in recent_thumbnail column - instant! 🚀
        // No loading needed, they come from the database
        const collectionsWithThumbnails = collectionsWithCounts;
        console.log('✅ Collections loaded with cached thumbnails (instant!)');
        
        // Only update if we don't already have collections or if the new data has more collections
        // Also check if a collection was just created to prevent overwriting
        setCollections(prev => {
          if (window.collectionJustCreated) {
            console.log('🔒 CollectionsPage: Collection just created, skipping database reload');
            return prev;
          }
          if (prev.length === 0 || (collectionsWithThumbnails && collectionsWithThumbnails.length > prev.length)) {
            console.log('📝 CollectionsPage: Updating collections from database with counts AND thumbnails');
            return collectionsWithThumbnails || [];
          } else {
            console.log('📝 CollectionsPage: Keeping existing collections, not overwriting');
            return prev;
          }
        });
      } catch (error) {
        console.error('❌ CollectionsPage: Failed to load collections:', error);
      }
    };

    loadCollections();
  }, [user?.id]);

  // Add a manual refresh function that can be called from outside
  const refreshCollections = async () => {
    if (!user?.id) return;
    
    try {
      console.log('🔄 CollectionsPage: Manual refresh triggered');
      const { data: topics } = await topicService.getUserTopics(user.id);
      console.log('📊 CollectionsPage: Manual refresh loaded:', topics?.length || 0, 'collections');
      
      // Add cached thumbnails
      const topicsWithThumbnails = await addCachedThumbnails(topics || []);
      
      // Refresh content counts for all collections
      const collectionsWithCounts = await refreshCollectionCounts(topicsWithThumbnails || []);
      
      setCollections(collectionsWithCounts);
      console.log('✅ CollectionsPage: Manual refresh completed with cached thumbnails');
    } catch (error) {
      console.error('❌ CollectionsPage: Manual refresh failed:', error);
    }
  };

  // Expose refresh function globally for debugging
  useEffect(() => {
    window.refreshCollections = refreshCollections;
    console.log('🔧 CollectionsPage: Manual refresh function available as window.refreshCollections()');
  }, [user?.id]);

  // Listen for global collection creation events
  useEffect(() => {
    const handleCollectionCreated = (event) => {
      console.log('📡 CollectionsPage: Received collectionCreated event:', event.detail);
      const { collection } = event.detail;
      
      if (collection) {
        console.log('✅ CollectionsPage: Adding new collection from global event:', collection);
        setCollections(prev => {
          const exists = prev.some(c => c.id === collection.id);
          if (exists) {
            console.log('⚠️ CollectionsPage: Collection already exists, skipping');
            return prev;
          }
          const updated = [collection, ...prev];
          console.log('📝 CollectionsPage: Updated collections count after global event:', updated.length);
          return updated;
        });
        
        // Set a flag to prevent reloading from database for a few seconds
        window.collectionJustCreated = true;
        setTimeout(() => {
          window.collectionJustCreated = false;
          console.log('🔒 CollectionsPage: Re-enabled database reloading');
        }, 5000);
      }
    };

    const handleContentAdded = (event) => {
      console.log('📡 CollectionsPage: Received contentAdded event:', event.detail);
      const { content, collection } = event.detail;
      
      if (content && collection) {
        console.log('✅ CollectionsPage: Content added to collection:', collection.name);
        // Refresh content counts for all collections
        setTimeout(async () => {
          console.log('🔄 Refreshing content counts after content added...');
          setCollections(prev => {
            refreshCollectionCounts(prev).then(updatedCollections => {
              setCollections(updatedCollections);
              console.log('✅ Content counts refreshed after content added');
            });
            return prev;
          });
        }, 1000);
      }
    };

    window.addEventListener('collectionCreated', handleCollectionCreated);
    window.addEventListener('contentAdded', handleContentAdded);
    console.log('📡 CollectionsPage: Listening for global collectionCreated and contentAdded events');

    return () => {
      window.removeEventListener('collectionCreated', handleCollectionCreated);
      window.removeEventListener('contentAdded', handleContentAdded);
    };
  }, []);


  // Load collection content using join table (2-query approach)
  // NEVER loads all items - only items in the specified collection
  const loadCollectionContentByJoinTable = useCallback(async (userId, collectionId, limit = 60) => {
    try {
      if (!userId || !collectionId) {
        console.error('[loadCollectionContentByJoinTable] Missing userId or collectionId');
        return { results: [], nextCursor: null };
      }

      console.log(`[loadCollectionContentByJoinTable] topic_id=${collectionId} user_id=${userId}`);

      // Query 1: Get item_ids from join table
      const { data: links, error: linksError } = await supabase
        .schema('public')
        .from('topic_items')
        .select('item_id, created_at')
        .eq('topic_id', collectionId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      // Guard: If Query 1 errors, return empty immediately
      if (linksError) {
        console.error('[loadCollectionContentByJoinTable] topic_items error:', linksError);
        return { results: [], nextCursor: null };
      }

      // Guard: If Query 1 returns 0 rows, return empty immediately (no fallback)
      const itemIds = (links || []).map(r => r.item_id).filter(Boolean);
      if (!itemIds || itemIds.length === 0) {
        console.log(`[loadCollectionContentByJoinTable] No items found for collection ${collectionId}`);
        return { results: [], nextCursor: null };
      }

      // Guard: Hard validation before Query 2
      if (!Array.isArray(itemIds) || itemIds.length === 0) {
        console.error('[loadCollectionContentByJoinTable] Invalid itemIds array');
        return { results: [], nextCursor: null };
      }

      // Query 2: Fetch items using item IDs
      const { data: items, error: itemsError } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('*')
        .in('id', itemIds)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (itemsError) {
        console.error('[loadCollectionContentByJoinTable] deepsearch.items error:', itemsError);
        return { results: [], nextCursor: null };
      }

      // Preserve join table ordering
      const orderIndex = new Map(itemIds.map((id, idx) => [id, idx]));
      const sorted = (items || []).slice().sort((a, b) => {
        return (orderIndex.get(a.id) ?? 999999) - (orderIndex.get(b.id) ?? 999999);
      });

      // Map to UI format
      const mapDeepsearchItemToUi = (item) => {
        const meta = item?.meta || {};
        const autoTags = item?.auto_tags || {};
        
        // Channel: Strict Title Case
        let channel = meta.channel;
        if (channel && typeof channel === 'string') {
          const channelLower = channel.toLowerCase();
          const channelMap = {
            'instagram': 'Instagram',
            'youtube': 'YouTube',
            'tiktok': 'TikTok',
            'podcast': 'Podcast',
            'web': 'Web',
            'twitter': 'Twitter',
            'linkedin': 'LinkedIn',
            'reddit': 'Reddit',
          };
          channel = channelMap[channelLower] || channel;
        }
        if (!channel && item.url) {
          const url = item.url.toLowerCase();
          if (url.includes('instagram.com')) channel = 'Instagram';
          else if (url.includes('youtube.com') || url.includes('youtu.be')) channel = 'YouTube';
          else if (url.includes('tiktok.com')) channel = 'TikTok';
          else if (url.includes('open.spotify.com') || url.includes('podcasts.apple.com')) channel = 'Podcast';
          else if (url.includes('x.com') || url.includes('twitter.com')) channel = 'Twitter';
          else if (url.includes('linkedin.com')) channel = 'LinkedIn';
          else if (url.includes('reddit.com')) channel = 'Reddit';
          else channel = 'Web';
        }
        if (!channel) channel = 'Web';

        // ContentType
        let contentType = meta.content_type;
        if (!contentType && item.source_type) {
          const validTypes = ['video', 'image', 'note', 'pdf', 'link', 'audio', 'carousel', 'reel'];
          contentType = validTypes.includes(item.source_type) ? item.source_type : 'link';
        }
        if (!contentType) contentType = 'link';

        return {
          id: item.id,
          title: item.title || null,
          description: item.description || null,
          url: item.url || null,
          thumbnail_url: item.thumbnail_url || item.image_url || meta.thumbnail_url || meta.thumbnail || null,
          image_url: item.image_url || item.thumbnail_url || null,
          created_at: item.created_at || null,
          source_type: item.source_type,
          contentType: contentType,
          channel: channel,
          auto_tags: autoTags || { topics: [] },
          primary_tags: item.primary_tags || [],
          extracted_text: item.extracted_text || item.raw_text || null,
          analysis_data: meta.analysis_data || meta.ai_analysis || autoTags || {},
          transcription: meta.transcription?.text || null,
          meta: meta,
          status: item.status,
          _source: 'public.topic_items + deepsearch.items',
          topic_id: collectionId,
        };
      };

      const mappedData = sorted.map(mapDeepsearchItemToUi).slice(0, limit);
      console.log(`[loadCollectionContentByJoinTable] Returning ${mappedData.length} items for collection ${collectionId}`);

      return { results: mappedData, nextCursor: null };
    } catch (error) {
      console.error('[loadCollectionContentByJoinTable] exception:', error);
      return { results: [], nextCursor: null };
    }
  }, []);

  // Load content for selected collection (using join table - no fallback to all items)
  useEffect(() => {
    const loadCollectionContent = async () => {
      if (!selectedCollection || !user?.id) return;
      
      // Hard block: "All" collection should not use this function
      // "All" should be handled separately if needed
      if (selectedCollection.name === 'All') {
        console.warn('[CollectionsPage] "All" collection should not use join table approach');
        setCollectionContent([]);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const result = await loadCollectionContentByJoinTable(user.id, selectedCollection.id, 60);
        setCollectionContent(result.results || []);
        
        // Update thumbnail for this collection from the first item
        if (result.results && result.results.length > 0) {
          const firstItem = result.results[0];
          const thumbnailUrl = firstItem?.thumbnail_url || firstItem?.image_url || firstItem?.url || null;
          if (thumbnailUrl) {
            setCollections(prev => prev.map(c => 
              c.id === selectedCollection.id 
                ? { ...c, recent_thumbnail: thumbnailUrl }
                : c
            ));
            // Cache it
            thumbnailCache.set(selectedCollection.id, {
              thumbnailUrl,
              timestamp: Date.now()
            });
          }
        }
      } catch (error) {
        console.error('Failed to load collection content:', error);
        setCollectionContent([]);
      } finally {
        setLoading(false);
      }
    };

    loadCollectionContent();
  }, [selectedCollection, user?.id, loadCollectionContentByJoinTable]);

  // Log collection content when it changes
  useEffect(() => {
    if (collectionContent && collectionContent.length > 0) {
      console.log(`[CollectionsPage] Rendering ${collectionContent.length} items`);
      console.log(`[CollectionsPage] First 3 items:`, collectionContent.slice(0, 3).map(item => ({
        id: item.id,
        url: item.url,
        title: item.title?.substring(0, 50)
      })));
    }
  }, [collectionContent]);

  // Merge realtime updates into collectionContent
  useEffect(() => {
    if (!selectedCollection || !realtimeItems || realtimeItems.length === 0) return;
    
    setCollectionContent(prev => {
      const updated = [...prev];
      let changed = false;
      
      realtimeItems.forEach(update => {
        // Only process items that belong to this collection
        if (update.topic_id === selectedCollection.id && update.user_id === user?.id) {
          const index = updated.findIndex(i => i && i.id === update.id);
          if (index !== -1) {
            // Update existing - merge carefully
            const existing = updated[index];
            updated[index] = { 
              ...existing, 
              ...update,
              // Preserve progress if update doesn't have it
              progress_percentage: update.progress_percentage !== undefined 
                ? update.progress_percentage 
                : existing.progress_percentage
            };
            changed = true;
          } else {
            // New item for this collection
            updated.unshift(update);
            changed = true;
          }
        }
      });
      
      return changed ? updated : prev;
    });
  }, [realtimeItems, selectedCollection?.id, user?.id]);

  const handleCollectionSelect = (collection) => {
    setSelectedCollection(collection);
  };

  const handleCreateCollection = async (e) => {
    e.preventDefault();
    console.log('Create collection clicked, user:', user);
    
    if (!newCollection.name.trim() || !user?.id) {
      console.log('Missing name or user ID:', { name: newCollection.name, userId: user?.id });
      console.error('Please make sure you are logged in and enter a collection name');
      return;
    }

    console.log('Creating collection with data:', {
      user_id: user.id,
      name: newCollection.name.trim(),
      description: newCollection.description.trim(),
      color: newCollection.color
    });

    setCreating(true);
    try {
      const { data: createdCollection, error } = await topicService.createTopic({
        user_id: user.id,
        name: newCollection.name.trim(),
        description: newCollection.description.trim(),
        color: newCollection.color
      });

      console.log('Create collection result:', { createdCollection, error });

      if (error) {
        console.error('Error creating collection:', error);
        const errorInfo = ErrorHandler.handleApiError(error, 'create collection');
        console.error(`Failed to create collection: ${errorInfo.userMessage}`);
        return;
      }

      if (createdCollection) {
        setCollections(prev => [...prev, createdCollection]);
        setNewCollection({ name: '', description: '', color: '#3B82F6' });
        setShowCreateModal(false);
      } else {
        console.error('Failed to create collection: No data returned from database');
      }
    } catch (error) {
      console.error('Failed to create collection:', error);
      
      // Development mode fallback when database is not available
      if (import.meta.env.DEV && (error.message?.includes('fetch') || error.message?.includes('network') || error.message?.includes('connection'))) {
        console.log('Using development mode fallback for collection creation');
        const mockCollection = {
          id: `mock-${Date.now()}`,
          user_id: user.id,
          name: newCollection.name.trim(),
          description: newCollection.description.trim(),
          color: newCollection.color,
          created_at: new Date().toISOString(),
          content_count: 0,
          conversation_count: 0
        };
        
        setCollections(prev => [...prev, mockCollection]);
        setNewCollection({ name: '', description: '', color: '#3B82F6' });
        setShowCreateModal(false);
        console.log('Collection created (development mode - not saved to database). Please set up your .env file with Supabase credentials for production use.');
        return;
      }
      
      console.error(`Failed to create collection: ${error.message || 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!user) return;

    setDeletingContent(contentId);
    try {
      const { error } = await contentService.deleteContent(user.id, contentId);
      if (error) throw error;

      // Remove from local state
      setCollectionContent(prev => prev.filter(item => item.id !== contentId));
    } catch (error) {
      console.error('Failed to delete content:', error);
    } finally {
      setDeletingContent(null);
      setShowDeleteModal(false);
      setContentToDelete(null);
    }
  };

  const confirmDelete = (content) => {
    setContentToDelete(content);
    setShowDeleteModal(true);
  };

  const handleDeleteCollectionWithItems = async () => {
    if (!collectionToDelete) return;
    
    setDeletingCollection(true);
    try {
      // Get all content in this collection
      const { data: collectionContent } = await contentService.getContentByCollection(user.id, collectionToDelete.id);
      
      if (collectionContent && collectionContent.length > 0) {
        // Delete all items first
        console.log(`🗑️ Deleting ${collectionContent.length} items from collection...`);
        for (const content of collectionContent) {
          const { error } = await contentService.deleteContent(user.id, content.id);
          if (error) {
            console.error(`Failed to delete content ${content.id}:`, error);
            // Continue deleting others even if one fails
          }
        }
        console.log(`✅ Deleted ${collectionContent.length} items from collection`);
      }
      
      // Now delete the collection itself
      const { error } = await topicService.deleteTopic(collectionToDelete.id);
      if (error) throw error;
      
      // Remove from local state
      setCollections(prev => prev.filter(c => c.id !== collectionToDelete.id));
      
      // If this was the selected collection, clear it
      if (selectedCollection?.id === collectionToDelete.id) {
        setSelectedCollection(null);
        setCollectionContent([]);
      }
      
      setShowDeleteCollectionModal(false);
      setCollectionToDelete(null);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Failed to delete collection:', error);
      alert(`Failed to delete collection: ${error.message || 'Unknown error'}`);
    } finally {
      setDeletingCollection(false);
    }
  };

  const getCollectionCount = (collectionId) => {
    const collection = collections.find(c => c.id === collectionId);
    return collection?.content_count || 0;
  };

  const colorOptions = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Orange', value: '#F59E0B' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Teal', value: '#14B8A6' }
  ];

  // Debug info
  console.log('CollectionsPage render - user authenticated:', !!user?.id);
  
  if (!user) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 relative overflow-hidden">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading...</h2>
            <p className="text-gray-600">Please wait while we load your collections.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#FAFAFA] relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-[#FAFAFA] to-[#F5F5F5]"></div>
      
      {/* Sidebar */}
      <Sidebar 
        isExpanded={isExpanded} 
        onToggle={() => setIsExpanded(!isExpanded)} 
      />
      
      {/* Arrow for minimized sidebar */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="fixed left-16 top-8 w-6 h-6 bg-white/40 backdrop-blur-lg rounded-r-lg flex items-center justify-center text-gray-800 hover:bg-white/50 transition-all duration-500 ease-out z-[60] shadow-lg border border-white/30"
        >
          <ChevronRight size={16} />
        </button>
      )}
      
      {/* Main Content */}
      <main className={`flex-1 flex flex-col transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`}>
        <div className="flex-1 p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            
            {/* Header */}
            <div className="flex items-center justify-between mb-12">
              <div>
                <h1 className="text-4xl font-light text-gray-900 mb-2 tracking-tight">Collections</h1>
                <p className="text-gray-500 text-sm font-light">Organize and curate your content</p>
              </div>
              
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Create Collection button clicked');
                  setShowCreateModal(true);
                }}
                className="bg-gray-900 text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-gray-800 transition-all duration-200 flex items-center space-x-2 cursor-pointer relative z-10 shadow-sm hover:shadow-md"
                style={{ 
                  pointerEvents: 'auto', 
                  position: 'relative',
                  zIndex: 999
                }}
              >
                <Plus size={16} />
                <span>New Collection</span>
              </button>
            </div>

            {/* Collections Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
              {collections.map((collection, index) => (
                <div
                  key={index}
                  className={`relative rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:border-gray-300 hover:shadow-md transition-all duration-200 text-left group cursor-pointer ${
                    selectedCollection?.id === collection.id ? 'border-gray-900 shadow-md ring-1 ring-gray-900' : ''
                  }`}
                  style={{ minHeight: '160px' }}
                  onClick={() => handleCollectionSelect(collection)}
                >
                  {/* Background Image */}
                  {collection.recent_thumbnail ? (
                    <div className="absolute inset-0">
                      <img
                        src={collection.recent_thumbnail}
                        alt={collection.name}
                        className="w-full h-full object-cover transition-opacity duration-300"
                        onLoad={(e) => {
                          e.target.style.opacity = '1';
                        }}
                        onError={(e) => {
                          // Hide broken image and show gradient
                          e.target.style.display = 'none';
                          const gradient = e.target.parentElement.nextElementSibling;
                          if (gradient) {
                            gradient.style.display = 'block';
                          }
                        }}
                        style={{ opacity: 0 }}
                      />
                    </div>
                  ) : null}
                  {/* Gradient fallback - always present but hidden when image loads */}
                  <div 
                    className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200"
                    style={{ display: collection.recent_thumbnail ? 'none' : 'block' }}
                  />
                  
                  {/* Dark Overlay */}
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/60 transition-colors" />
                  
                  {/* Content */}
                  <div className="relative z-10 p-5 h-full flex flex-col justify-between">
                    {/* Top: Delete Button */}
                    <div className="flex justify-end">
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const itemCount = getCollectionCount(collection.id);
                          
                          // If collection has no items, delete immediately
                          if (itemCount === 0) {
                            try {
                              const { error } = await topicService.deleteTopic(collection.id);
                              if (error) throw error;
                              
                              // Remove from local state
                              setCollections(prev => prev.filter(c => c.id !== collection.id));
                              
                              // If this was the selected collection, clear it
                              if (selectedCollection?.id === collection.id) {
                                setSelectedCollection(null);
                                setCollectionContent([]);
                              }
                            } catch (error) {
                              console.error('Failed to delete collection:', error);
                              alert(`Failed to delete collection: ${error.message || 'Unknown error'}`);
                            }
                          } else {
                            // Collection has items, show confirmation modal
                            setCollectionToDelete(collection);
                            setShowDeleteCollectionModal(true);
                            setDeleteConfirmText('');
                          }
                        }}
                        className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded transition-all opacity-0 group-hover:opacity-100"
                        title="Delete collection"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    
                    {/* Bottom: Title and Count */}
                    <div>
                      <h3 className="font-medium text-white text-base mb-1 drop-shadow-lg">
                        {collection.name}
                      </h3>
                      <p className="text-xs text-white/80 drop-shadow">
                        {getCollectionCount(collection.id)} items
                      </p>
                      {collection.description && (
                        <p className="text-xs text-white/70 mt-1.5 line-clamp-2 drop-shadow">
                          {collection.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Collection Content */}
            {selectedCollection && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8">
                <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100">
                  <div className="flex items-center space-x-4">
                    {/* Elegant folder icon with color indicator */}
                    <div className="relative">
                      <Folder className="w-8 h-8 text-gray-400" />
                      {/* Small color dot */}
                      <div
                        className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white"
                        style={{ backgroundColor: selectedCollection.color || '#A78BFA' }}
                      />
                    </div>
                    <div>
                      <h2 className="text-2xl font-light text-gray-900 tracking-tight">{selectedCollection.name}</h2>
                      <p className="text-sm text-gray-500 mt-1">{collectionContent.length} items</p>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Loading content...</span>
                  </div>
                ) : collectionContent.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {collectionContent.map((item, index) => (
                      <div 
                        key={index} 
                        className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:shadow-md hover:border-gray-300 transition-all duration-200 cursor-pointer relative group"
                      >

                        {/* Clickable Content */}
                        <div 
                          onClick={() => {
                            setSelectedContent(item);
                            setShowContentModal(true);
                          }}
                          className="cursor-pointer"
                        >
                        {/* Thumbnail */}
                        <div className="w-full h-40 bg-gray-50 relative overflow-hidden mb-3 rounded-md group">
                          {item.thumbnail_url ? (
                            <img 
                              src={item.thumbnail_url} 
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-4xl">{item.platform_icon || '📄'}</span>
                            </div>
                          )}
                          
                          {/* Delete Button - Top Left Corner */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDelete(item);
                            }}
                            disabled={deletingContent === item.id}
                            className="absolute top-2 left-2 p-1.5 bg-white/95 hover:bg-white text-gray-600 hover:text-gray-900 rounded transition-all opacity-0 group-hover:opacity-100 z-20 disabled:opacity-50 shadow-sm"
                            title="Delete content"
                          >
                            <X size={14} />
                          </button>
                          
                          {/* Type Badge - Top Right */}
                          <div className="absolute top-2 right-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              item.content_type === 'video' || item.content_type === 'reel' ? 'bg-red-50 text-red-700' :
                              item.content_type === 'image' || item.content_type === 'photo' || item.content_type === 'carousel' ? 'bg-blue-50 text-blue-700' :
                              item.channel === 'Instagram' ? 'bg-pink-50 text-pink-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {item.content_type === 'video' || item.content_type === 'reel' ? 'Video' : 
                               item.content_type === 'image' || item.content_type === 'photo' || item.content_type === 'carousel' ? 'Image' :
                               item.channel === 'Instagram' ? 'Instagram' : 
                               'Content'}
                            </span>
                          </div>
                        </div>

                        {/* Title - Editable */}
                        {editingTitle === item.id ? (
                          <div className="mb-2 flex items-center space-x-2">
                            <input
                              type="text"
                              value={editingTitleValue}
                              onChange={(e) => setEditingTitleValue(e.target.value)}
                              onBlur={async () => {
                                if (editingTitleValue.trim() && editingTitleValue !== item.title) {
                                  try {
                                    const { error } = await contentService.updateContent(item.id, {
                                      title: editingTitleValue.trim()
                                    });
                                    if (error) throw error;
                                    // Update local state
                                    setCollectionContent(prev => prev.map(content =>
                                      content.id === item.id 
                                        ? { ...content, title: editingTitleValue.trim() }
                                        : content
                                    ));
                                  } catch (error) {
                                    console.error('Failed to update title:', error);
                                    alert('Failed to update title. Please try again.');
                                  }
                                }
                                setEditingTitle(null);
                                setEditingTitleValue('');
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                } else if (e.key === 'Escape') {
                                  setEditingTitle(null);
                                  setEditingTitleValue('');
                                }
                              }}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <h4 
                            className="text-sm font-medium text-gray-900 mb-1.5 line-clamp-2 cursor-pointer hover:text-gray-600 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTitle(item.id);
                              setEditingTitleValue(item.title);
                            }}
                            title="Click to edit title"
                          >
                            {item.title || 'Untitled'}
                          </h4>
                        )}
                        
                        {/* Description */}
                        {item.description && (
                          <p className="text-xs text-gray-500 line-clamp-2 mb-2 leading-relaxed">
                            {item.description}
                          </p>
                        )}
                        
                        {/* Tags */}
                        {(item.tags && item.tags.length > 0) || (item.analysis_data?.tags && item.analysis_data.tags.length > 0) ? (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {(item.tags || item.analysis_data?.tags || []).slice(0, 2).map((tag, i) => (
                              <span
                                key={i}
                                className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        
                        {/* Date */}
                        {item.created_at && (
                          <p className="text-xs text-gray-400">
                            {new Date(item.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        )}

                        {/* Processing Status with Progress Bar */}
                        {item.analysis_status === 'processing' && (
                          <div className="mb-2">
                            <ProgressBar 
                              percentage={item.progress_percentage || 0}
                              status={item.progress_status || 'processing'}
                              showIcon={true}
                              showLabel={true}
                              size="sm"
                            />
                          </div>
                        )}

                        {/* AI Summary */}
                        {item.ai_analysis?.summary && (
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{item.ai_analysis.summary}</p>
                        )}

                        {/* Key Points Preview */}
                        {item.key_points && item.key_points.length > 0 && (
                          <div className="mb-3">
                            <div className="text-xs text-gray-500 mb-1 font-medium">Key Points:</div>
                            <ul className="text-xs text-gray-600 space-y-1">
                              {item.key_points.slice(0, 2).map((point, pointIndex) => (
                                <li key={pointIndex} className="flex items-start">
                                  <span className="text-blue-500 mr-1">•</span>
                                  <span className="line-clamp-1">{point}</span>
                                </li>
                              ))}
                              {item.key_points.length > 2 && (
                                <li className="text-gray-400 text-xs">
                                  +{item.key_points.length - 2} more points
                                </li>
                              )}
                            </ul>
                          </div>
                        )}

                        {/* Channel Badge */}
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-sm">{item.platform_icon}</span>
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                            {item.channel}
                          </span>
                        </div>

                        {/* Tags */}
                        {item.tags && item.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {item.tags.slice(0, 3).map((tag, tagIndex) => (
                              <span key={tagIndex} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {tag}
                              </span>
                            ))}
                            {item.tags.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                +{item.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Status and Date */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {item.analysis_status === 'completed' && (
                              <span className="text-xs text-green-600">✓ Analyzed</span>
                            )}
                            {item.transcription?.full_transcript && (
                              <span className="text-xs text-blue-600">📝 Transcript</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {new Date(item.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="relative inline-block mb-4">
                      <Folder className="w-12 h-12 text-gray-300 mx-auto" />
                      {/* Small color dot */}
                      <div
                        className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white"
                        style={{ backgroundColor: selectedCollection.color || '#A78BFA' }}
                      />
                    </div>
                    <h3 className="text-base font-medium text-gray-900 mb-1">No content yet</h3>
                    <p className="text-sm text-gray-500">Add content to this collection to see it here</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create Collection Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              console.log('Modal backdrop clicked, closing modal');
              setShowCreateModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-medium text-gray-900">Create Collection</h2>
                <p className="text-sm text-gray-500 mt-1">Organize your content</p>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreateCollection} className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Name</label>
                <input
                  type="text"
                  value={newCollection.name}
                  onChange={(e) => setNewCollection({ ...newCollection, name: e.target.value })}
                  placeholder="Collection name"
                  className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5 uppercase tracking-wide">Description</label>
                <textarea
                  value={newCollection.description}
                  onChange={(e) => setNewCollection({ ...newCollection, description: e.target.value })}
                  placeholder="Optional description"
                  className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 resize-none h-20 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Color</label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setNewCollection({ ...newCollection, color: color.value })}
                      className={`w-8 h-8 rounded-md border-2 transition-all ${
                        newCollection.color === color.value 
                          ? 'border-gray-900 scale-105' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors border border-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newCollection.name.trim()}
                  className="flex-1 px-4 py-2 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Plus size={14} />
                      <span>Create</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Content Detail Modal */}
      <ContentDetailModal
        isOpen={showContentModal}
        onClose={() => {
          setShowContentModal(false);
          setSelectedContent(null);
        }}
        content={selectedContent}
      />


      {/* Delete Collection Confirmation Modal - Only shown if collection has items */}
      {showDeleteCollectionModal && collectionToDelete && getCollectionCount(collectionToDelete.id) > 0 && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 border border-gray-200">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Delete Collection</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete <strong>"{collectionToDelete.name}"</strong>?
              </p>
              <div className="bg-red-50 border border-red-100 rounded-md p-3 mb-4">
                <p className="text-sm text-red-800 font-medium mb-1.5">
                  Warning: This collection contains {getCollectionCount(collectionToDelete.id)} item(s)
                </p>
                <p className="text-xs text-red-700">
                  All items in this collection will be permanently deleted.
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type <strong className="text-red-600">delete</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type 'delete' to confirm"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && deleteConfirmText.toLowerCase() === 'delete') {
                      e.preventDefault();
                      handleDeleteCollectionWithItems();
                    }
                  }}
                />
              </div>
              <p className="text-sm text-gray-500">
                All content and analysis data in this collection will be permanently removed.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteCollectionModal(false);
                  setCollectionToDelete(null);
                  setDeleteConfirmText('');
                }}
                className="flex-1 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors border border-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCollectionWithItems}
                disabled={deletingCollection || deleteConfirmText.toLowerCase() !== 'delete'}
                className="flex-1 px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {deletingCollection ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={14} />
                    <span>Delete Collection</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Content Confirmation Modal */}
      {showDeleteModal && contentToDelete && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 border border-gray-200">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <X size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Delete Content</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2 text-sm">
                Are you sure you want to delete <strong>"{contentToDelete.title}"</strong>?
              </p>
              <p className="text-xs text-gray-500">
                This will permanently remove the content and all its analysis data.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setContentToDelete(null);
                }}
                className="flex-1 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors border border-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteContent(contentToDelete.id)}
                disabled={deletingContent === contentToDelete.id}
                className="flex-1 px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {deletingContent === contentToDelete.id ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <X size={14} />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionsPage;
